// Fokusfang — die Tastatur-Zusagen eines modalen Dialogs, EINMAL.
//
// Herkunft: dieser Haken ist aus `components/ui/Rueckfrage.tsx` herausgezogen, wo er als drei
// Effekte im Dialog selbst stand. Der Anlass ist gemessen, nicht theoretisch: die drei Dialoge der
// Mediathek (`components/admin/BunnyCDNManager.tsx`) fangen den Fokus NICHT. Im gebauten Stand
// nachgemessen — EIN Shift+Tab aus dem Eingabefeld des offenen Ordner-Dialogs setzt den Fokus auf
// „Ausgewählte löschen", einen Knopf HINTER dem Schleier. Mit der Tastatur steht man dann auf einem
// Bedienelement, das man nicht sieht, und kann dort auslösen, was man nicht liest.
//
// ⚠️ Warum ein geteilter Haken und kein zweiter handgebauter Fang: die drei Insel-Dialoge sind
// FORMULAR-Dialoge (Titel, Eingabefeld, zwei Knöpfe) — die `Rueckfrage` kann sie nicht ersetzen,
// sie kennt nur Titel und Text. Geteilt werden kann aber genau das, woran es fehlt. Ein zweiter
// Fang wäre die nächste Handkopie gewesen, und zwar eine besonders teure: er hätte den Fall unten
// (zwei offene Dialoge) mit hoher Wahrscheinlichkeit falsch gelöst, weil man ihn beim Bauen eines
// einzelnen Dialogs gar nicht sieht.
//
// Was der Haken zusagt — jede Zusage ist in `components/ui/__tests__/Rueckfrage.test.tsx` und in
// `components/admin/__tests__/BunnyCDNManager.test.tsx` je einzeln belegt:
//
// - Tab am letzten und Shift+Tab am ersten Ziel laufen im Kreis, sie wandern nicht hinter den
//   Dialog;
// - ein bereits entwichener Fokus wird beim nächsten Tab zurückgeholt;
// - Escape schließt — und zwar nur den obersten Dialog, nicht das Formular darunter;
// - der Fokus kehrt beim Abbau auf das auslösende Element zurück;
// - beim Aufbau bekommt ein benanntes Element den Fokus, sonst das erste Ziel im Dialog.
import { useEffect, useRef, type RefObject } from 'react';

/** Was den Fokusfang trägt. Bewusst ohne Sichtbarkeitsfilter — siehe Begründung am Fangen selbst. */
const FOKUSSIERBAR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Ein angemeldeter Fang. Das Objekt selbst ist der Ausweis — verglichen wird mit `!==`, nicht über
 * eine Kennung, die zwei Dialoge versehentlich teilen könnten.
 */
interface Eintrag {
  fläche: RefObject<HTMLElement | null>;
}

/**
 * Die offenen Fänge in der Reihenfolge ihrer Öffnung. Nur der OBERSTE handelt.
 *
 * ⚠️ Das ist kein Beiwerk, sondern der Grund, warum es diesen Haken überhaupt geteilt gibt. In der
 * Mediathek können zwei modale Schichten gleichzeitig offen stehen: ein Insel-Dialog und darüber
 * die Rückfrage vor dem Löschen der Auswahl (`z-50` gegen `z-[55]`, siehe dort). Beide Lauscher
 * hängen am selben Knoten (`document`, Einfangphase) — und `stopPropagation` hilft dagegen NICHT,
 * denn es hält nur andere KNOTEN auf, nicht weitere Lauscher am selben. Ohne diesen Stapel liefe
 * also:
 *   • Escape schlösse BEIDE Dialoge auf einen Druck;
 *   • Tab zöge den Fokus erst in den unteren Dialog (dessen Fang sieht den Fokus „außerhalb") und
 *     erst der später angemeldete Lauscher zöge ihn wieder heraus — das Ergebnis hinge allein an
 *     der Anmeldereihenfolge.
 * Beides sieht man einem einzeln gebauten Dialog nicht an. Die Regel dagegen ist eine Zeile: der
 * zuletzt geöffnete Dialog besitzt die Tastatur.
 */
const stapel: Eintrag[] = [];

interface FokusfangWunsch {
  /**
   * Was Escape auslöst. Darf bei jedem Rendern eine neue Pfeilfunktion sein — der Wert wandert
   * über eine Referenz in den Lauscher.
   *
   * ⚠️ Genau deshalb steht er NICHT in den Abhängigkeiten des Effekts: der Lauscher hängt an
   * `document` und soll GENAU EINMAL pro offenem Dialog an- und abgemeldet werden. Stünde er
   * drin, meldete sich der Lauscher bei jedem Rendern des Aufrufers neu an — und ein Aufrufer,
   * der eine Pfeilfunktion direkt hineinschreibt, täte das bei jedem Tastendruck.
   */
  beiEscape: () => void;
  /**
   * Welches Element beim Aufbau den Fokus bekommt. Ohne Angabe das erste Ziel im Dialog.
   *
   * Die Rückfrage benennt hier ausdrücklich ihren Abbrechen-Knopf: „nicht der Gefahren-Knopf" ist
   * dort eine Zusage und keine Folge der Reihenfolge im Baum. Die Formular-Dialoge der Mediathek
   * lassen die Angabe weg — bei ihnen IST das erste Ziel das Eingabefeld, und mehr will die
   * Nutzerin dort nicht.
   *
   * ⚠️ Nicht durch Reacts `autoFocus` zu ersetzen: das setzt den Fokus schon in der Einbauphase,
   * also VOR dem Effekt, der sich das auslösende Element merkt. Der Dialog merkte sich dann sein
   * eigenes Eingabefeld als Auslöser und gäbe den Fokus beim Schließen an einen Knoten zurück, den
   * es nicht mehr gibt — die Rückkehr fiele still aus. Genau so stand es in den drei
   * Insel-Dialogen (`autoFocus` am Eingabefeld), bevor sie diesen Haken bekamen.
   */
  anfangsfokus?: RefObject<HTMLElement | null>;
  /**
   * Wechselt dieser Wert, wird der Anfangsfokus erneut gesetzt. Für Dialoge, die zwischen zwei
   * Inhalten montiert BLEIBEN: die Rückfrage stellt so ihre zweite Frage wieder auf „Abbrechen",
   * statt den Fokus dort zu lassen, wo ihn die erste hinterlassen hat — unter Umständen also auf
   * „Löschen".
   */
  anfangsschlüssel?: unknown;
}

/**
 * Hängt den Fokusfang an die zurückgegebene Fläche. „Offen" heißt „montiert": der Aufrufer rendert
 * den Dialog hinter seiner eigenen Weiche, und Aufbau/Abbau tragen Merken, Setzen und Zurückgeben.
 */
export const useFokusfang = <T extends HTMLElement>({
  beiEscape,
  anfangsfokus,
  anfangsschlüssel,
}: FokusfangWunsch): RefObject<T | null> => {
  const flächeRef = useRef<T | null>(null);
  const beiEscapeRef = useRef(beiEscape);
  beiEscapeRef.current = beiEscape;

  // ── Fokus merken und zurückgeben ────────────────────────────────────────────────────────────
  // Steht VOR dem Setz-Effekt: Effekte laufen in Schreibreihenfolge, also liest dieser hier noch
  // das auslösende Element, bevor der nächste den Fokus in den Dialog zieht.
  useEffect(() => {
    const auslöser = document.activeElement;
    return () => {
      // ⚠️ Auf Anwesenheit prüfen: das auslösende Element ist häufig der Löschen-Knopf einer
      // Zeile, die es nach der bestätigten Löschung nicht mehr gibt. `focus()` auf einem
      // abgehängten Knoten ist folgenlos, der Fokus fiele still auf `<body>` — und die Nutzerin
      // stünde beim nächsten Tab wieder ganz am Anfang der Seite. Dann lieber nichts tun und den
      // Fokus dort lassen, wo die Liste ihn nach dem Neuladen hinlegt.
      if (auslöser instanceof HTMLElement && document.contains(auslöser)) auslöser.focus();
    };
  }, []);

  // ── Anfangsfokus ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const ziel =
      anfangsfokus?.current ?? flächeRef.current?.querySelector<HTMLElement>(FOKUSSIERBAR) ?? null;
    ziel?.focus();
  }, [anfangsfokus, anfangsschlüssel]);

  // ── Escape und Fokusfang ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const eintrag: Eintrag = { fläche: flächeRef };
    stapel.push(eintrag);

    const beiTaste = (e: KeyboardEvent) => {
      // Nur der oberste Dialog hört zu. Begründung am Stapel.
      if (stapel[stapel.length - 1] !== eintrag) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        // ⚠️ `stopPropagation` in der Einfangphase an `document`: React hängt seine Lauscher an den
        // Wurzelknoten DARUNTER. Ohne das erreichte dasselbe Escape auch das Formular, aus dem der
        // Dialog geöffnet wurde — ein Druck schlösse Dialog UND Formular, und die Eingabe der
        // Nutzerin wäre weg. Escape gehört dem obersten Dialog.
        e.stopPropagation();
        beiEscapeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const fläche = flächeRef.current;
      if (!fläche) return;
      // Kein Sichtbarkeitsfilter (`offsetParent`, `getClientRects`): jsdom rechnet kein Layout, ein
      // solcher Filter verwürfe dort JEDES Ziel und der Fang wäre im Test wirkungslos — grün,
      // ohne je etwas zu fangen.
      const ziele = Array.from(fläche.querySelectorAll<HTMLElement>(FOKUSSIERBAR));
      if (ziele.length === 0) return;

      const erste = ziele[0];
      const letzte = ziele[ziele.length - 1];
      const aktiv = document.activeElement;

      // Fokus außerhalb (etwa nach einem Klick in die Browser-Leiste): zurückholen statt weiterlaufen
      // lassen — das ist der Fall, den ein Fang am Dialog-Element selbst NICHT sähe.
      if (!fläche.contains(aktiv)) {
        e.preventDefault();
        (e.shiftKey ? letzte : erste).focus();
        return;
      }
      if (e.shiftKey && aktiv === erste) {
        e.preventDefault();
        letzte.focus();
      } else if (!e.shiftKey && aktiv === letzte) {
        e.preventDefault();
        erste.focus();
      }
    };

    document.addEventListener('keydown', beiTaste, true);
    return () => {
      document.removeEventListener('keydown', beiTaste, true);
      const platz = stapel.indexOf(eintrag);
      if (platz !== -1) stapel.splice(platz, 1);
    };
  }, []);

  return flächeRef;
};
